import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, ChevronUp, ChevronDown, ArrowLeft } from "lucide-react";
import api from "../lib/api";

/**
 * KidsReels — TikTok-style vertical scrollable feed of kid-safe reels.
 * Mirrors the main /shorts experience but filtered to is_kids_safe only.
 */
export default function KidsReels() {
  const [items, setItems] = useState([]);
  const [i, setI] = useState(0);
  const videoRef = useRef(null);

  useEffect(() => {
    api.get("/kids-public/reels?limit=40").then((r) => setItems(r.data?.reels || [])).catch(() => {});
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowUp") setI((n) => Math.max(0, n - 1));
      else if (e.key === "ArrowDown" || e.key === " ") setI((n) => Math.min(items.length - 1, n + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [items.length]);

  const v = items[i];
  const src = v && (v.hls_url || v.embed_url || v.video_url);
  const isEmbed = src && /youtube|youtu\.be|vimeo|tiktok/i.test(src);

  return (
    <div data-testid="kids-reels-page" className="fixed inset-0 bg-black flex items-center justify-center">
      <Link to="/kids" data-testid="kids-reels-back" className="fixed top-4 left-4 z-20 w-10 h-10 rounded-full bg-white/80 text-black flex items-center justify-center shadow-lg"><ArrowLeft className="w-5 h-5" /></Link>
      <img src="/kids-learn-play-logo.png" alt="" className="fixed top-3 right-4 z-10 w-20 h-auto opacity-90 drop-shadow-2xl pointer-events-none" />

      <div className="absolute right-4 top-1/2 -translate-y-1/2 z-20 flex flex-col gap-2">
        <button
          data-testid="kreels-up"
          onClick={() => setI((n) => Math.max(0, n - 1))}
          className="w-12 h-12 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center"
        ><ChevronUp className="w-6 h-6" /></button>
        <button
          data-testid="kreels-down"
          onClick={() => setI((n) => Math.min(items.length - 1, n + 1))}
          className="w-12 h-12 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center"
        ><ChevronDown className="w-6 h-6" /></button>
      </div>

      <div className="relative w-full h-full max-w-[440px] md:max-w-[560px] mx-auto bg-black">
        {v
          ? isEmbed
            ? <iframe key={v.id} src={src} className="w-full h-full" allow="autoplay; encrypted-media" allowFullScreen title={v.title} />
            : src
              ? <video key={v.id} ref={videoRef} src={src} autoPlay loop playsInline controls className="w-full h-full object-cover" />
              : (
                <div className={`w-full h-full flex flex-col items-center justify-center bg-gradient-to-br ${v.color || "from-pink-400 to-fuchsia-500"} p-6`}>
                  <div className="text-[10rem] leading-none drop-shadow-2xl">{v.emoji || "⭐"}</div>
                  <div className="font-display font-black text-3xl text-white text-center mt-4">{v.title}</div>
                </div>
              )
          : <div className="text-white/60 text-center" data-testid="kreels-empty">No kid reels yet — be the first!</div>}
        {v && (
          <div className="absolute bottom-6 left-4 right-24 text-white">
            <div className="font-display font-black text-xl drop-shadow">{v.title}</div>
            <Link to={`/kids/timeline/${v.creator_id}`} className="text-white/85 text-sm hover:underline drop-shadow inline-flex items-center gap-1">
              <span>{v.creator_avatar || "⭐"}</span> @{v.handle || v.creator_name}
            </Link>
            <div className="mt-2 inline-block text-xs uppercase tracking-[0.2em] font-black bg-emerald-500/80 px-2 py-0.5 rounded">KID APPROVED ✓</div>
          </div>
        )}
      </div>
    </div>
  );
}
